import React, { useState } from 'react';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import { Typography } from '@/UIlibrary/Typography/Typography';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import {
	useRequestRegistryCorrectionsMutation,
	RequestRegistryCorrectionsInput,
	RequestRegistryCorrectionsMutation
} from '@/graphql/__generated__/onboarding';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import logger from '@/logger';
import toast from 'react-hot-toast';
import { ChangesRequestEntry } from '../RegistryDetails';
import { pascalToConstantCase } from '@/utils/utils';
import * as Sentry from '@sentry/react';

const RequestCorrectionsModal: React.FC<{
	isOpen: boolean;
	onClose: () => void;
	changesRequested: ChangesRequestEntry[];
	clientId: string;
	onSuccess?: () => void;
}> = ({ isOpen, onClose, changesRequested, clientId, onSuccess }) => {
	const t = useTranslations('RegistryDetails');
	const [onboardingChangesDescription, setOnboardingChangesDescription] = useState('');
	const [descriptionError, setDescriptionError] = useState('');

	const onboardingClient = useApolloClient(SCHEMAS.ONBOARDING);

	const [requestRegistryCorrections, { loading }] = useRequestRegistryCorrectionsMutation({
		client: onboardingClient,
		onCompleted: async (data: RequestRegistryCorrectionsMutation) => {
			const requestCorrections = data.requestRegistryCorrections;
			if (requestCorrections?.successful) {
				onClose();
				if (onSuccess) {
					onSuccess();
				}
			}
			if (requestCorrections?.mutationErrors && requestCorrections.mutationErrors.length > 0) {
				let hasFieldErrors = false;

				requestCorrections.mutationErrors.forEach((error) => {
					if (error) {
						Sentry.captureException(error);
						switch (error.__typename) {
							case 'InvalidInput':
								logger.error('Invalid input', error.field);
								if (error.field === 'onboardingChangesDescription') {
									setDescriptionError(error.message || t('DescriptionRequired'));
									hasFieldErrors = true;
								}
								break;
							case 'InvalidAction':
								logger.error('Action failed', error.message);
								break;
							default:
								logger.error('Unhandled mutation error', error);
								break;
						}
					}
				});

				if (!hasFieldErrors) {
					toast.error(t('ErrorRequestingChanges'));
				}
			}
		},
		onError: (error) => {
			logger.error('Error requesting changes', error);
			toast.error(t('ErrorRequestingChanges'));
		}
	});

	const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		setOnboardingChangesDescription(value);

		if (descriptionError) {
			setDescriptionError('');
		}
	};

	const handleRequestCorrections = async () => {
		setDescriptionError('');
		if (!onboardingChangesDescription) {
			setDescriptionError(t('DescriptionRequired'));
			return;
		}

		const parsedChangesRequested: { [key: string]: string[] } = {};

		changesRequested.map((item) => {
			if (!parsedChangesRequested[item.section]) {
				parsedChangesRequested[item.section] = [];
			}
			parsedChangesRequested[item.section].push(pascalToConstantCase(item.field));
		});
		logger.debug('Requested changes', parsedChangesRequested);
		if (clientId) {
			const input: RequestRegistryCorrectionsInput = {
				clientId,
				onboardingChangesDescription,
				...parsedChangesRequested
			};
			await requestRegistryCorrections({ variables: { input } });
		}
	};

	return (
		<ConfirmModal
			isOpen={isOpen}
			confirmActionText={t('Send')}
			title={t('ConfirmRequestChangesTitle')}
			onClose={onClose}
			onCancel={onClose}
			onConfirm={handleRequestCorrections}
			maxWidth={600}
			subtitle={t('ConfirmRequestChangesSubtitle')}
			cancelBtnLoading={loading}
			confirmBtnLoading={loading}
		>
			<div className="flex flex-col gap-4 py-4">
				<div className="flex flex-col gap-2">
					{changesRequested.map((change) => (
						<div className="flex flex-row gap-1 items-center justify-between" key={change.field}>
							<Typography>{t(toTranslationKey(change.field))}:</Typography>
							<Typography weight="semibold">{change.value}</Typography>
						</div>
					))}
				</div>
				<div className="flex flex-col gap-1">
					<Typography size="md">{t('DescriptionLabel')}</Typography>
					<TextArea
						rows={4}
						value={onboardingChangesDescription}
						onChange={handleDescriptionChange}
						placeholder={t('EnterChangesDescription')}
						error={!!descriptionError}
						hint={
							descriptionError ||
							t('DescriptionLength') + '. ' + onboardingChangesDescription.length + '/3000'
						}
						maxChars={3000}
						strictMaxChars
					/>
				</div>
			</div>
		</ConfirmModal>
	);
};

export default RequestCorrectionsModal;
